/**
 * Right-side chat panel. Always visible by design. Read-only assistant — 5
 * tools (CT-1..CT-5). Conversation persists per user across sessions.
 *
 * Built on Cloudscape chat primitives: ChatBubble + Avatar for turns,
 * SupportPromptGroup for the empty-state suggestions, PromptInput for the
 * composer. react-markdown still renders assistant content (Cloudscape has
 * no markdown renderer).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ExpandableSection,
  Header,
  PromptInput,
  SpaceBetween,
  StatusIndicator,
} from "@cloudscape-design/components";
import { Avatar, ChatBubble, SupportPromptGroup } from "@cloudscape-design/chat-components";
import ReactMarkdown from "react-markdown";
import { ChatMessage, ToolTrace, loadChatHistory, sendChat } from "@/core/api/chat";

interface ChatPanelProps {
  onClose?: () => void;
}

const EXAMPLE_PROMPTS = [
  { id: "reports", text: "What reports do we have?" },
  { id: "open", text: "What's open?" },
  { id: "rcm", text: "Find RCM controls for SoD on payroll" },
] as const;

export function ChatPanel({ onClose }: ChatPanelProps = {}) {
  const [thread, setThread] = useState<ChatMessage[]>([]);
  const [pendingTraces, setPendingTraces] = useState<Record<number, ToolTrace[]>>({});
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    try {
      setThread(await loadChatHistory());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [thread.length]);

  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || busy) return;
      setBusy(true);
      setError(null);
      // Optimistic user turn.
      setThread((t) => [...t, { role: "user", content: text }]);
      setInput("");
      try {
        const reply = await sendChat(text);
        setThread(reply.thread);
        if (reply.tool_traces.length) {
          setPendingTraces((m) => ({ ...m, [reply.thread.length - 1]: reply.tool_traces }));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [busy],
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(127,127,127,0.2)" }}>
        <Header
          variant="h2"
          actions={
            onClose && <Button onClick={onClose} iconName="close" variant="icon" ariaLabel="Close chat" />
          }
        >
          Chat
        </Header>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "12px", minHeight: 0 }}>
        <SpaceBetween size="s">
          {thread.length === 0 && (
            <SpaceBetween size="xs">
              <Box color="text-body-secondary">Ask about reports, RCM, or findings — or try:</Box>
              <SupportPromptGroup
                ariaLabel="Suggested prompts"
                alignment="vertical"
                items={EXAMPLE_PROMPTS.map((p) => ({ id: p.id, text: p.text }))}
                onItemClick={({ detail }) => {
                  const prompt = EXAMPLE_PROMPTS.find((p) => p.id === detail.id);
                  if (prompt) void send(prompt.text);
                }}
              />
            </SpaceBetween>
          )}
          {thread.map((m, i) => (
            <ChatTurn key={`${i}-${m.created_at ?? ""}`} message={m} traces={pendingTraces[i]} />
          ))}
          {busy && (
            <ChatBubble
              type="incoming"
              ariaLabel="Assistant is responding"
              showLoadingBar
              avatar={
                <Avatar
                  color="gen-ai"
                  iconName="gen-ai"
                  loading
                  ariaLabel="Generative AI assistant"
                  tooltipText="Assistant"
                />
              }
            >
              <Box color="text-body-secondary">Thinking…</Box>
            </ChatBubble>
          )}
          <div ref={bottomRef} />
        </SpaceBetween>
      </div>

      {error && (
        <Box padding="xs">
          <StatusIndicator type="error">{error}</StatusIndicator>
        </Box>
      )}

      <div style={{ borderTop: "1px solid rgba(127,127,127,0.2)", padding: "8px" }}>
        <PromptInput
          value={input}
          onChange={({ detail }) => setInput(detail.value)}
          onAction={() => void send(input)}
          placeholder="Ask about reports, RCM, or findings…"
          actionButtonIconName="send"
          disabled={busy}
          minRows={1}
          maxRows={4}
          ariaLabel="Chat message"
          i18nStrings={{ actionButtonAriaLabel: "Send message" }}
        />
      </div>
    </div>
  );
}

function ChatTurn({ message, traces }: { message: ChatMessage; traces?: ToolTrace[] }) {
  const isUser = message.role === "user";
  const shown = message.tool_traces?.length ? message.tool_traces : traces;
  return (
    <ChatBubble
      type={isUser ? "outgoing" : "incoming"}
      ariaLabel={isUser ? "You" : "Assistant"}
      avatar={
        isUser ? (
          <Avatar ariaLabel="You" tooltipText="You" />
        ) : (
          <Avatar
            color="gen-ai"
            iconName="gen-ai"
            ariaLabel="Generative AI assistant"
            tooltipText="Assistant"
          />
        )
      }
    >
      <div className="chat-markdown">
        <ReactMarkdown>{message.content}</ReactMarkdown>
      </div>
      {shown && shown.length > 0 && <ToolTracesView traces={shown} />}
    </ChatBubble>
  );
}

function ToolTracesView({ traces }: { traces: ToolTrace[] }) {
  return (
    <ExpandableSection
      variant="footer"
      headerText={`${traces.length} tool call${traces.length === 1 ? "" : "s"}`}
    >
      <SpaceBetween size="xxs">
        {traces.map((t, i) => (
          <Box key={i} variant="small" color="text-body-secondary">
            <strong>{t.name}</strong>({JSON.stringify(t.input)}) → <code>{t.output_preview}</code>
          </Box>
        ))}
      </SpaceBetween>
    </ExpandableSection>
  );
}
